import React, { useState, useRef, useEffect, memo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import api from '../services/api';
import BrandAttribution from './shared/BrandAttribution';

// ============================================================================
// ANIMATED LIGHT BACKGROUND
// ============================================================================
const LightGradientBackground = memo(() => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none">
    {/* Base gradient - soft white to light purple */}
    <div className="absolute inset-0 bg-gradient-to-br from-white via-[#f8f7ff] to-[#f0eeff]" />

    {/* Floating gradient orbs - soft and subtle */}
    <div
      className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-60 blur-[100px] animate-blob"
      style={{ background: 'radial-gradient(circle, rgba(112,112,245,0.3) 0%, transparent 70%)' }}
    />
    <div
      className="absolute top-1/2 -left-40 w-[500px] h-[500px] rounded-full opacity-50 blur-[100px] animate-blob animation-delay-2000"
      style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.25) 0%, transparent 70%)' }}
    />
    <div
      className="absolute -bottom-40 right-1/4 w-[550px] h-[550px] rounded-full opacity-40 blur-[100px] animate-blob animation-delay-4000"
      style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.3) 0%, transparent 70%)' }}
    />

    {/* Subtle grid pattern */}
    <div
      className="absolute inset-0 opacity-[0.03]"
      style={{
        backgroundImage: `linear-gradient(#7070F5 1px, transparent 1px), linear-gradient(90deg, #7070F5 1px, transparent 1px)`,
        backgroundSize: '50px 50px'
      }}
    />
  </div>
));

// ============================================================================
// FLOATING SHAPES
// ============================================================================
const FloatingShapes = memo(() => {
  const shapes = [
    { type: 'circle', size: 8, x: 10, y: 20, delay: 0 },
    { type: 'circle', size: 6, x: 85, y: 15, delay: 1 },
    { type: 'circle', size: 10, x: 90, y: 70, delay: 2 },
    { type: 'circle', size: 5, x: 15, y: 75, delay: 0.5 },
    { type: 'circle', size: 7, x: 80, y: 85, delay: 1.5 },
    { type: 'square', size: 8, x: 5, y: 50, delay: 2.5 },
    { type: 'square', size: 6, x: 95, y: 40, delay: 3 },
  ];

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      {shapes.map((shape, i) => (
        <div
          key={i}
          className={`absolute ${shape.type === 'circle' ? 'rounded-full' : 'rounded-lg rotate-45'}
                      bg-gradient-to-br from-[#7070F5]/20 to-[#8B5CF6]/10 backdrop-blur-sm
                      border border-[#7070F5]/10`}
          style={{
            width: shape.size * 4,
            height: shape.size * 4,
            left: `${shape.x}%`,
            top: `${shape.y}%`,
            animation: `float-shape 8s ease-in-out infinite`,
            animationDelay: `${shape.delay}s`,
          }}
        />
      ))}
    </div>
  );
});

// ============================================================================
// ELEGANT CARD
// ============================================================================
const ElegantCard = memo(({ children, className = '' }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const cardRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setMousePosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  }, []);

  return (
    <div
      ref={cardRef}
      className={`relative group ${className}`}
      onMouseMove={handleMouseMove}
    >
      {/* Subtle glow effect following mouse */}
      <div
        className="absolute -inset-px rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: `radial-gradient(400px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(112,112,245,0.1), transparent 40%)`,
        }}
      />

      {/* Card with glass effect */}
      <div className="relative bg-white/80 backdrop-blur-xl rounded-3xl border border-white/60 shadow-[0_8px_32px_rgba(112,112,245,0.1)] overflow-hidden">
        {/* Top shine effect */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white to-transparent" />
        {children}
      </div>
    </div>
  );
});

// ============================================================================
// INPUT FIELD WITH ICON
// ============================================================================
const InputField = memo(({
  icon,
  type = 'text',
  value,
  onChange,
  placeholder,
  disabled,
  showPasswordToggle,
  onTogglePassword,
  isPasswordVisible
}) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className={`relative group transition-all duration-300 ${isFocused ? 'transform scale-[1.01]' : ''}`}>
      {/* Glow effect on focus */}
      <div className={`absolute -inset-0.5 rounded-xl bg-gradient-to-r from-[#7070F5] to-[#8B5CF6] opacity-0 blur-sm transition-opacity duration-300 ${isFocused ? 'opacity-20' : ''}`} />

      <div className="relative flex items-center">
        {/* Input — no inline icons, clean text area */}
        <input
          type={showPasswordToggle ? (isPasswordVisible ? 'text' : 'password') : type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          className="w-full bg-gray-50/80 border border-gray-200 rounded-xl py-4 px-4 text-gray-800 placeholder-gray-400
                     focus:outline-none focus:border-[#7070F5]/50 focus:bg-white focus:shadow-[0_0_0_4px_rgba(112,112,245,0.1)]
                     transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        />

        {/* Password toggle */}
        {showPasswordToggle && (
          <button
            type="button"
            onClick={onTogglePassword}
            className="absolute right-4 text-gray-400 hover:text-[#7070F5] transition-colors duration-300"
          >
            {isPasswordVisible ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            )}
          </button>
        )}
      </div>
    </div>
  );
});

// ============================================================================
// GRADIENT BUTTON
// ============================================================================
const GradientButton = memo(({ children, onClick, disabled, loading }) => {
  const buttonRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = useCallback((e) => {
    if (!buttonRef.current || disabled) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    setPosition({ x: x * 0.1, y: y * 0.1 });
  }, [disabled]);

  const handleMouseLeave = useCallback(() => {
    setPosition({ x: 0, y: 0 });
    setIsHovered(false);
  }, []);

  return (
    <button
      ref={buttonRef}
      onClick={onClick}
      disabled={disabled || loading}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      className={`relative w-full py-4 px-6 rounded-xl font-semibold text-lg overflow-hidden
                  transition-all duration-300 transform
                  ${disabled || loading
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-[#7070F5] to-[#8B5CF6] text-white shadow-lg shadow-[#7070F5]/25 hover:shadow-xl hover:shadow-[#7070F5]/30 hover:scale-[1.02] active:scale-[0.98]'
                  }`}
      style={{ transform: `translate(${position.x}px, ${position.y}px)` }}
    >
      {/* Animated shine effect */}
      {!disabled && !loading && (
        <div
          className={`absolute inset-0 transition-transform duration-700 ${isHovered ? 'translate-x-full' : '-translate-x-full'}`}
          style={{
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
          }}
        />
      )}

      {/* Content */}
      <span className="relative z-10 flex items-center justify-center gap-2">
        {loading ? (
          <>
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span>Signing in...</span>
          </>
        ) : (
          <>
            <span>Sign In</span>
            <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </>
        )}
      </span>
    </button>
  );
});

// ============================================================================
// MAIN LOGIN COMPONENT
// ============================================================================
const Login = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  // Password reset state (from email link)
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetComplete, setResetComplete] = useState(false);

  useEffect(() => {
    setMounted(true);
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const isReset = params.get('reset');
    const isVerified = params.get('verified');

    // Handle password reset token
    if (token && isReset) {
      setShowResetForm(true);
      setResetToken(token);
    }

    // Handle email verification token
    if (token && isVerified) {
      api.post('/auth/verify-email', { token })
        .then(() => {
          toast.success('Email verified successfully! You can now sign in.');
          window.history.replaceState({}, '', '/login');
        })
        .catch((err) => {
          const msg = err.response?.data?.error || 'Verification failed. The link may have expired.';
          toast.error(msg);
          window.history.replaceState({}, '', '/login');
        });
    }
  }, []);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail.trim()) { toast.error('Please enter your email'); return; }
    setResetLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: resetEmail.trim() });
      setResetSent(true);
      toast.success('Check your email for the reset link');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setResetLoading(true);
    try {
      await api.post('/auth/reset-password', { token: resetToken, new_password: newPassword });
      setResetComplete(true);
      toast.success('Password reset! You can now sign in.');
      // Clean URL
      window.history.replaceState({}, '', '/login');
    } catch (err) {
      const msg = err.response?.data?.error || 'Reset failed. The link may have expired.';
      toast.error(msg);
    } finally {
      setResetLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!username || !password) {
      toast.error('Please enter your email and password');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/login', {
        username,
        password,
      });

      if (rememberMe) {
        localStorage.setItem('greenflow-remember', 'true');
      }

      onLogin(response.data.token, response.data.user);
      toast.success('Welcome to GreenFlow Verifier!');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 overflow-hidden">
      {/* Animated Background */}
      <LightGradientBackground />
      <FloatingShapes />

      {/* Keyframe animations */}
      <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(30px, -20px) scale(1.05); }
          50% { transform: translate(-20px, 30px) scale(0.95); }
          75% { transform: translate(20px, 10px) scale(1.02); }
        }
        .animate-blob { animation: blob 25s ease-in-out infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }

        @keyframes float-shape {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }

        @keyframes pulse-soft {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        @keyframes float-logo {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
      `}</style>

      {/* Login Card */}
      <div
        className={`relative w-full max-w-md transition-all duration-1000 ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <ElegantCard>
          <div className="p-8 sm:p-10">
            {/* Logo and Header */}
            <div className="text-center mb-8">
              {/* GreenFlow Logo */}
              <div className="relative inline-block mb-6">
                <div className="absolute inset-0 rounded-2xl bg-[#7070F5]/20 blur-xl" style={{ animation: 'pulse-soft 3s ease-in-out infinite' }} />
                <div
                  className="relative"
                  style={{ animation: 'float-logo 4s ease-in-out infinite' }}
                >
                  <img
                    src="/greenflow_logo.png"
                    alt="GreenFlow Logo"
                    className="w-24 h-24 rounded-2xl shadow-lg shadow-[#7070F5]/30 object-contain"
                  />
                </div>
              </div>

              {/* GreenFlow text logo */}
              <h1 className="text-3xl font-bold mb-2">
                <span className="bg-gradient-to-r from-[#7070F5] via-[#8B5CF6] to-[#7C3AED] bg-clip-text text-transparent">
                  GreenFlow
                </span>
              </h1>
              <p className="text-gray-500">
                Powering Africa's Energy Verification
              </p>
            </div>

            {/* Forgot Password View */}
            {showForgotPassword && !showResetForm && (
              <div className="space-y-5">
                <h2 className="text-xl font-bold text-gray-800 text-center">Reset your password</h2>
                <p className="text-sm text-gray-500 text-center">Enter your email and we'll send you a reset link.</p>
                {resetSent ? (
                  <div className="text-center space-y-4">
                    <div className="mx-auto w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-gray-600">Check your email for the reset link.</p>
                    <button type="button" onClick={() => { setShowForgotPassword(false); setResetSent(false); }}
                      className="text-[#7070F5] hover:text-[#5858d6] font-medium text-sm bg-transparent border-none cursor-pointer">
                      Back to sign in
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <input type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="w-full bg-gray-50/80 border border-gray-200 rounded-xl py-4 px-4 text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#7070F5]/50 focus:bg-white focus:shadow-[0_0_0_4px_rgba(112,112,245,0.1)] transition-all duration-300" />
                    <button type="submit" disabled={resetLoading || !resetEmail.trim()}
                      className={`w-full py-4 rounded-xl font-semibold text-lg transition-all duration-300 ${resetLoading || !resetEmail.trim() ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-[#7070F5] to-[#8B5CF6] text-white shadow-lg shadow-[#7070F5]/25 hover:shadow-xl'}`}>
                      {resetLoading ? 'Sending...' : 'Send Reset Link'}
                    </button>
                    <button type="button" onClick={() => setShowForgotPassword(false)}
                      className="w-full text-center text-sm text-gray-500 hover:text-gray-700 bg-transparent border-none cursor-pointer py-2">
                      Back to sign in
                    </button>
                  </form>
                )}
              </div>
            )}

            {/* Reset Password View (from email link) */}
            {showResetForm && (
              <div className="space-y-5">
                <h2 className="text-xl font-bold text-gray-800 text-center">Choose a new password</h2>
                {resetComplete ? (
                  <div className="text-center space-y-4">
                    <div className="mx-auto w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-gray-600">Password reset successfully!</p>
                    <button type="button" onClick={() => { setShowResetForm(false); setResetComplete(false); }}
                      className="text-[#7070F5] hover:text-[#5858d6] font-semibold text-sm bg-transparent border-none cursor-pointer">
                      Sign in with new password
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleResetPassword} className="space-y-4">
                    <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                      placeholder="New password (minimum 8 characters)"
                      className="w-full bg-gray-50/80 border border-gray-200 rounded-xl py-4 px-4 text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#7070F5]/50 focus:bg-white focus:shadow-[0_0_0_4px_rgba(112,112,245,0.1)] transition-all duration-300" />
                    <button type="submit" disabled={resetLoading || newPassword.length < 8}
                      className={`w-full py-4 rounded-xl font-semibold text-lg transition-all duration-300 ${resetLoading || newPassword.length < 8 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-[#7070F5] to-[#8B5CF6] text-white shadow-lg shadow-[#7070F5]/25 hover:shadow-xl'}`}>
                      {resetLoading ? 'Resetting...' : 'Reset Password'}
                    </button>
                  </form>
                )}
              </div>
            )}

            {/* Login Form */}
            {!showForgotPassword && !showResetForm && (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email Field */}
              <div className={`transition-all duration-700 delay-100 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <InputField
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  }
                  type="email"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="you@company.com"
                  disabled={loading}
                />
              </div>

              {/* Password Field */}
              <div className={`transition-all duration-700 delay-200 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <InputField
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  }
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  disabled={loading}
                  showPasswordToggle
                  isPasswordVisible={showPassword}
                  onTogglePassword={() => setShowPassword(!showPassword)}
                />
              </div>

              {/* Remember Me & Forgot Password */}
              <div className={`flex items-center justify-between text-sm transition-all duration-700 delay-300 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                <label className="flex items-center gap-2 cursor-pointer group">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded border-2 transition-all duration-300 flex items-center justify-center
                      ${rememberMe
                        ? 'bg-[#7070F5] border-[#7070F5]'
                        : 'border-gray-300 group-hover:border-[#7070F5]/50'
                      }`}
                    >
                      {rememberMe && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-gray-600 group-hover:text-gray-800 transition-colors">Remember me</span>
                </label>

                <button type="button" onClick={() => { setShowForgotPassword(true); setResetEmail(username); }}
                  className="text-[#7070F5] hover:text-[#5858d6] font-medium transition-colors bg-transparent border-none cursor-pointer text-sm">
                  Forgot password?
                </button>
              </div>

              {/* Submit Button */}
              <div className={`pt-2 transition-all duration-700 delay-400 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                <GradientButton
                  onClick={handleSubmit}
                  disabled={!username || !password}
                  loading={loading}
                />
              </div>
            </form>
            )}

            {/* Divider */}
            <div className={`relative my-8 transition-all duration-700 delay-500 ${mounted ? 'opacity-100' : 'opacity-0'}`}>
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-400">or</span>
              </div>
            </div>

            {/* Register Link */}
            <div className={`text-center transition-all duration-700 delay-600 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <p className="text-gray-600">
                Don't have an account?{' '}
                <Link
                  to="/register"
                  className="text-[#7070F5] hover:text-[#5858d6] font-semibold transition-colors"
                >
                  Register now
                </Link>
              </p>
            </div>

            {/* Back to Home */}
            <div className={`mt-6 text-center transition-all duration-700 delay-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-gray-600 text-sm transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to home
              </Link>
            </div>
          </div>
        </ElegantCard>

        {/* Security Badge */}
        <div className={`mt-6 flex justify-center transition-all duration-700 delay-800 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/60 backdrop-blur-sm border border-gray-200/50 text-gray-500 text-sm shadow-sm">
            <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span>Secured with 256-bit encryption</span>
          </div>
        </div>

        {/*
          Company attribution, at the foot of the page where a reader expects it.
          A visitor who is not signed in is the one most likely to be a funder,
          registry reviewer or prospective partner deciding whether this is a
          real company, and this was the only screen they could reach. The
          statutory detail stays folded until asked for, so the sign-in task
          keeps the page.
        */}
        <div className={`mt-6 flex justify-center transition-all duration-700 delay-1000 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <BrandAttribution variant="lockup" tone="light" align="center" />
        </div>
      </div>
    </div>
  );
};

export default memo(Login);
